import { z } from 'zod'

// The client's own domains as its CMS shows them (Settings → Domains). The console holds the Cloudflare token and owns
// every domain change: the CMS asks it through the site's API, which signs a hook request (`POST /api/hooks/domains` on
// the console, with the site's CONTROL_SECRET). The console checks the package again on every request.

/**
 * Where a domain is, in words for the client: `waiting` for the client's nameservers, `connecting` (the zone is live
 * and our team connects it), `active` (the site answers on it), or `problem` (our team is looking at it).
 */
export const CLIENT_DOMAIN_STATES = ['waiting', 'connecting', 'active', 'problem'] as const
export const ClientDomainStateSchema = z.enum(CLIENT_DOMAIN_STATES)
export type ClientDomainState = z.infer<typeof ClientDomainStateSchema>

export const ClientDomainSchema = z.object({
  id: z.string(),
  domain: z.string(),
  state: ClientDomainStateSchema,
  /** The two nameservers to set at the registrar. */
  nameServers: z.array(z.string()),
  /** The site's address (only staff change it). */
  isPrimary: z.boolean(),
  /** The mailbox's domain: locked, it can only be changed on request. */
  isMail: z.boolean(),
  /** Whether the client may remove it: an extra domain, neither the primary nor the mail domain. */
  removable: z.boolean(),
  createdAt: z.string(),
})
export type ClientDomain = z.infer<typeof ClientDomainSchema>

export const CLIENT_REQUEST_KINDS = ['domain_help', 'mail_domain_change'] as const
export const ClientRequestKindSchema = z.enum(CLIENT_REQUEST_KINDS)
export type ClientRequestKind = z.infer<typeof ClientRequestKindSchema>
export const CLIENT_REQUEST_STATUSES = ['open', 'done', 'rejected'] as const
export const ClientRequestStatusSchema = z.enum(CLIENT_REQUEST_STATUSES)
export type ClientRequestStatus = z.infer<typeof ClientRequestStatusSchema>

export const ClientRequestSchema = z.object({
  id: z.string(),
  kind: ClientRequestKindSchema,
  status: ClientRequestStatusSchema,
  note: z.string(),
  domain: z.string().nullable(),
  /** Staff's answer. */
  staffNote: z.string(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
})
export type ClientRequest = z.infer<typeof ClientRequestSchema>

/** Why the client cannot add a domain now, if it cannot. */
export const ClientDomainBlockSchema = z.enum(['content_scope', 'not_in_package', 'at_limit', 'not_connected'])
export type ClientDomainBlock = z.infer<typeof ClientDomainBlockSchema>

/** The whole Domains screen. */
export const ClientDomainsSchema = z.object({
  package: z.string(),
  /** The package's domain limit with the site's overrides (0 when the package has no own domain). */
  limit: z.number().int(),
  used: z.number().int(),
  blocked: ClientDomainBlockSchema.nullable(),
  /** Whether the package has the mailbox (so the first connected domain becomes the mail domain). */
  mailbox: z.boolean(),
  /** The mail domain, once one is set. */
  mailDomain: z.string().nullable(),
  /** The primary first, then in the order they were added. */
  domains: z.array(ClientDomainSchema),
  /** The latest requests, newest first. */
  requests: z.array(ClientRequestSchema),
})
export type ClientDomains = z.infer<typeof ClientDomainsSchema>

const Note = z.string().trim().max(1000)

/** The CMS's requests (POST /api/admin/domains…). */
export const ClientAddDomainRequestSchema = z.object({ domain: z.string().trim().min(1).max(253) })
export const ClientHelpRequestSchema = z.object({ note: Note.default(''), domain: z.string().trim().max(253).optional() })
export const ClientMailChangeRequestSchema = z.object({
  note: Note.min(1),
  /** The domain the client wants as its mail domain, if it knows. */
  domain: z.string().trim().max(253).optional(),
  /** The client ticked the warning: old addresses stop receiving mail and their mail is deleted. */
  acknowledged: z.literal(true),
})
export type ClientHelpRequest = z.infer<typeof ClientHelpRequestSchema>
export type ClientMailChangeRequest = z.infer<typeof ClientMailChangeRequestSchema>

/** Who asked, as the site names them to the console. */
const ActorSchema = z.object({ name: z.string().max(200), email: z.string().max(320) })

/** The signed hook body the site sends the console. */
export const ConsoleDomainHookSchema = z.object({
  site: z.string().min(1).max(100),
  actor: ActorSchema.optional(),
  op: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('list') }),
    z.object({ kind: z.literal('add'), domain: z.string().trim().min(1).max(253) }),
    z.object({ kind: z.literal('check'), domainId: z.string().min(1).max(64) }),
    z.object({ kind: z.literal('remove'), domainId: z.string().min(1).max(64) }),
    z.object({ kind: z.literal('help'), note: Note, domain: z.string().trim().max(253).optional() }),
    z.object({ kind: z.literal('mail_change'), note: Note.min(1), domain: z.string().trim().max(253).optional(), acknowledged: z.literal(true) }),
  ]),
})
export type ConsoleDomainHook = z.infer<typeof ConsoleDomainHookSchema>
export type ConsoleDomainOp = ConsoleDomainHook['op']
